import React, { Component } from "react";
import PropTypes from "prop-types";
import { get } from "lodash";
import { btoa } from "isomorphic-base64";
import { WebEngage, DeepBI } from "../../../../../../isomorphic/integrations";
import Icon from "../../../../atoms/icon";
import styles from "./magazine-inline-player.m.css";
import ResponsiveImageWithFallback from "../../../../atoms/responsive-image-with-fallback";
import { generateImageSources } from "../../../../utils/utils";

class MagazineInlinePlayer extends Component {
  constructor(props) {
    super(props);
    this.state = {
      playing: false,
      currentTime: 0,
      duration: 0,
      selectedSpeed: 1
    };
    this.audioRef = React.createRef();
    this.hls = null;
  }

  componentDidMount() {
    this.loadAndInit();
  }

  componentWillUnmount() {
    if (this.hls) {
      this.hls.destroy();
      this.hls = null;
    }
  }

  loadAndInit = () => {
    const { story, collection } = this.props;
    const storyId = get(story, ["id"]);
    const magazineID = get(collection, ["id"]);
    if (!storyId || !magazineID) return;

    const encodedMagazineID = btoa(magazineID);
    const audioUrl = `https://audiomag.vikatan.com/${encodedMagazineID}/${storyId}/${storyId}.m3u8`;

    const initPlayer = () => {
      if (window.Hls && window.Hls.isSupported()) {
        if (this.hls) this.hls.destroy();
        this.hls = new window.Hls();
        this.hls.loadSource(audioUrl);
        this.hls.attachMedia(this.audioRef.current);
        this.hls.on(window.Hls.Events.MANIFEST_PARSED, () => {
          this.audioRef.current.playbackRate = this.state.selectedSpeed;
        });
      } else if (this.audioRef.current.canPlayType("application/vnd.apple.mpegurl")) {
        this.audioRef.current.src = audioUrl;
        this.audioRef.current.load();
      }
    };

    if (window.Hls) {
      initPlayer();
      return;
    }
    const script = document.createElement("script");
    script.src = "https://cdn.jsdelivr.net/npm/hls.js@latest";
    script.async = true;
    script.onload = initPlayer;
    document.body.appendChild(script);
  };

  handlePlay = () => {
    const { story, collection } = this.props;
    const audioElement = this.audioRef.current;
    if (!audioElement) return;
    const isResumed = audioElement.currentTime > 0;
    const eventName = isResumed ? "audiomagazineresumed" : "audiomagazineplayed";
    audioElement
      .play()
      .then(() => {
        this.setState({ playing: true });
        WebEngage.Webplayer(story, eventName, collection);
        DeepBI.PingAudioplayer(story, eventName, collection, this.formatSecondsTime(audioElement.currentTime));
      })
      .catch(err => {
        console.error("[InlinePlayer] Playback error:", err);
        this.setState({ playing: false });
      });
  };

  handlePause = () => {
    const { story, collection } = this.props;
    const audioElement = this.audioRef.current;
    if (!audioElement) return;
    audioElement.pause();
    this.setState({ playing: false, currentTime: audioElement.currentTime });
    WebEngage.Webplayer(story, "audiomagazinepaused", collection);
    DeepBI.PingAudioplayer(story, "audiomagazinepaused", collection, this.formatSecondsTime(audioElement.currentTime));
  };

  handleSeek = e => {
    this.audioRef.current.currentTime = e.target.value;
    this.setState({ currentTime: Number(e.target.value) });
  };

  handleTimeUpdate = () => {
    this.setState({
      currentTime: this.audioRef.current.currentTime,
      duration: this.audioRef.current.duration || 0
    });
  };

  handleEnded = () => {
    const { story, collection } = this.props;
    this.setState({ playing: false, currentTime: 0 });
    this.audioRef.current.currentTime = 0;
    WebEngage.Webplayer(story, "audiomagazinecompleted", collection);
    DeepBI.PingAudioplayer(story, "audiomagazinecompleted", collection, 0);
  };

  skip = direction => {
    const { story, collection } = this.props;
    const audioElement = this.audioRef.current;
    if (!audioElement) return;
    if (direction === "back") {
      audioElement.currentTime = Math.max(0, audioElement.currentTime - 15);
      WebEngage.Webplayer(story, "audiomagazineseekbackword", collection);
      DeepBI.PingAudioplayer(
        story,
        "audiomagazineseekbackword",
        collection,
        this.formatSecondsTime(audioElement.currentTime)
      );
    } else {
      audioElement.currentTime = Math.min(audioElement.duration, audioElement.currentTime + 15);
      WebEngage.Webplayer(story, "audiomagazineseekforward", collection);
      DeepBI.PingAudioplayer(
        story,
        "audiomagazineseekforward",
        collection,
        this.formatSecondsTime(audioElement.currentTime)
      );
    }
  };

  handleSpeedChange = () => {
    const { story, collection } = this.props;
    const speeds = [1, 1.2, 1.5];
    const { selectedSpeed } = this.state;
    const currentIndex = speeds.indexOf(selectedSpeed);
    const newSpeed = currentIndex === speeds.length - 1 ? speeds[0] : speeds[currentIndex + 1];
    this.audioRef.current.playbackRate = newSpeed;
    this.setState({ selectedSpeed: newSpeed });
    WebEngage.Webplayer(story, "audiomagazinespeed", collection);
    DeepBI.PingAudioplayer(
      story,
      "audiomagazinespeed",
      collection,
      this.formatSecondsTime(this.audioRef.current.currentTime)
    );
  };

  handleShare = () => {
    const { story } = this.props;
    const slug = get(story, ["slug"], "");
    const domainName = global && global.location && global.location.origin;
    const url = `${domainName}/${slug}`;
    if (navigator.share) {
      navigator.share({ title: document.title, url }).catch(err => console.error(err));
    } else {
      navigator.clipboard.writeText(url).catch(err => console.error(err));
    }
  };

  formatTime = time => {
    const timeNum = Number(time);
    if (isNaN(timeNum)) return "00:00";
    const hours = Math.floor(timeNum / 3600);
    const minutes = Math.floor((timeNum % 3600) / 60);
    const seconds = Math.floor(timeNum % 60);
    const hh = hours > 0 ? `${hours.toString().padStart(2, "0")}:` : "";
    return `${hh}${minutes.toString().padStart(2, "0")}:${seconds.toString().padStart(2, "0")}`;
  };

  formatSecondsTime = time => {
    const minutes = Math.floor((time % 3600) / 60);
    const seconds = Math.floor(time % 60);
    return parseInt(minutes.toString().padStart(2, "0")) * 60 + parseInt(seconds.toString().padStart(2, "0"));
  };

  render() {
    const { playing, currentTime, duration, selectedSpeed } = this.state;
    const { story } = this.props;
    const storyId = get(story, ["id"]);
    const storyHeadline = get(story, ["headline"], "");
    const storyImage = get(story, ["hero-image-s3-key"], "");

    return (
      <div className={styles["inline-player"]}>
        <audio
          id={`inline-audio-${storyId}`}
          ref={this.audioRef}
          onTimeUpdate={this.handleTimeUpdate}
          onEnded={this.handleEnded}
          preload="none"
        />

        {/* ===== DESKTOP ===== */}
        <div className={`${styles["desktop-player"]} hidden-mobile`}>
          {/* Image */}
          <div className={styles["player-image"]}>
            <ResponsiveImageWithFallback
              className={styles["image-wrapper"]}
              slug={storyImage}
              metadata={{}}
              alt={storyHeadline}
              imgParams={{ auto: ["format", "compress"] }}
              sources={generateImageSources(
                { aspectRatio: [4, 3], screenWidthCoverage: 0.1 },
                { aspectRatio: [4, 3], screenWidthCoverage: 0.06 }
              )}
            />
          </div>

          <div className={styles["player-body"]}>
            {/* Headline */}
            <div className={styles["player-headline"]}>{storyHeadline && <p>{storyHeadline}</p>}</div>

            <div className={styles["player-controls"]}>
              {/* Controls */}
              <div className={styles["desktop-controls"]}>
                {/* <div className={styles["control-btn"]} onClick={() => this.skip("back")}>
              <Icon type="icon-audio-previous" className={styles["icon"]} />
            </div> */}
                <div className={styles["control-btn"]} onClick={() => this.skip("back")}>
                  <Icon type="backward" className={styles["icon"]} />
                </div>
                <div className={styles["play-btn"]} onClick={playing ? this.handlePause : this.handlePlay}>
                  <Icon type={playing ? "pause" : "play"} className={styles["play-icon"]} />
                </div>
                <div className={styles["control-btn"]} onClick={() => this.skip("fwd")}>
                  <Icon type="forward" className={styles["icon"]} />
                </div>
                {/* <div className={styles["control-btn"]}>
              <Icon type="icon-audio-next" className={styles["icon"]} />
            </div> */}
              </div>

              {/* Progress + Time */}
              <div className={styles["desktop-progress"]}>
                <span className={styles["time"]}>{this.formatTime(currentTime)}</span>
                <input
                  type="range"
                  min={0}
                  max={duration || 0}
                  step={0.01}
                  value={currentTime}
                  onChange={this.handleSeek}
                  className={styles["progress"]}
                />
                <span className={styles["time"]}>{this.formatTime(duration)}</span>
              </div>

              {/* Speed */}
              <div className={styles["speed-section"]} onClick={this.handleSpeedChange}>
                <span className={styles["speed-btn"]}>{selectedSpeed}x</span>
                <span className={styles["speed-label"]}>Speed</span>
              </div>
              <div className={styles["control-btn"]} onClick={this.handleShare}>
                <Icon type="icon-share" className={styles["icon"]} />
              </div>
            </div>
          </div>
        </div>

        {/* ===== MOBILE ===== */}
        <div className={`${styles["mobile-player"]} hidden-desktop`}>
          {/* Top row: image + headline */}
          <div className={styles["mobile-top"]}>
            <div className={styles["player-image"]}>
              <ResponsiveImageWithFallback
                className={styles["image-wrapper"]}
                slug={storyImage}
                metadata={{}}
                alt={storyHeadline}
                imgParams={{ auto: ["format", "compress"] }}
                sources={generateImageSources(
                  { aspectRatio: [4, 3], screenWidthCoverage: 0.2 },
                  { aspectRatio: [4, 3], screenWidthCoverage: 0.15 }
                )}
              />
            </div>
            <div className={styles["mobile-headline"]}>
              <marquee>{storyHeadline}</marquee>
            </div>
          </div>

          {/* Progress bar */}
          <div className={styles["mobile-progress"]}>
            <span className={styles["time"]}>{this.formatTime(currentTime)}</span>
            <input
              type="range"
              min={0}
              max={duration || 0}
              step={0.01}
              value={currentTime}
              onChange={this.handleSeek}
              className={styles["progress"]}
            />
            <span className={styles["time"]}>{this.formatTime(duration)}</span>
          </div>

          {/* Controls row */}
          <div className={styles["mobile-controls"]}>
            {/* <div className={styles["control-btn"]} onClick={() => this.skip("back")}>
              <Icon type="icon-audio-previous" className={styles["icon"]} />
            </div> */}
            <div className={styles["control-btn"]} onClick={() => this.skip("back")}>
              <Icon type="backward" className={styles["icon"]} />
            </div>
            <div className={styles["play-btn"]} onClick={playing ? this.handlePause : this.handlePlay}>
              <Icon type={playing ? "pause" : "play"} className={styles["play-icon"]} />
            </div>
            <div className={styles["control-btn"]} onClick={() => this.skip("fwd")}>
              <Icon type="forward" className={styles["icon"]} />
            </div>
            {/* <div className={styles["control-btn"]}>
              <Icon type="icon-audio-next" className={styles["icon"]} />
            </div> */}
            <div className={styles["speed-btn"]} onClick={this.handleSpeedChange}>
              {selectedSpeed}x
            </div>
            <div className={styles["control-btn"]} onClick={this.handleShare}>
              <Icon type="icon-share" className={styles["icon"]} />
            </div>
          </div>
        </div>
      </div>
    );
  }
}

MagazineInlinePlayer.propTypes = {
  story: PropTypes.object.isRequired,
  collection: PropTypes.object.isRequired
};

export default MagazineInlinePlayer;
